/**
 * Sorting utility for assembly entries.
 *
 * Sorts entries to match the anatomical rendering order used by PartSelectorList:
 *   proximal parts → left-side parts → right-side parts → other parts.
 */

import type { IAssemblyEntry } from './assembly-types';
import type { PartType } from './types';

/** Part types rendered for both left and right sides. */
const SIDED_PART_TYPES = new Set<PartType>(['bifurcated_down', 'contra', 'ipsi']);

/** Anatomical sort order within a section (mirrors PART_TYPE_ORDER in part-selector-list). */
const PART_TYPE_ORDER: Record<PartType, number> = {
    bifurcated_up: 0,
    cuff: 1,
    contra: 2,
    ipsi: 3,
    bifurcated_down: 4,
    standard: 5,
    occluder: 6,
};

/**
 * Compute a numeric sort key matching PartSelectorList rendering order:
 *   0–99   → proximal parts (side = none)
 *   100–199 → left-side parts
 *   200–299 → right-side parts
 *   300–399 → other parts (side = none)
 */
function assemblySortKey(entry: IAssemblyEntry): number {
    const typeOrder = PART_TYPE_ORDER[entry.partType] ?? 99;

    if (entry.partType === 'bifurcated_up' || entry.partType === 'cuff') {
        return typeOrder;
    }
    if (SIDED_PART_TYPES.has(entry.partType)) {
        const sideOffset = entry.side === 'left' ? 100 : 200;
        return sideOffset + typeOrder;
    }
    return 300 + typeOrder;
}

/**
 * Group assembly entries by catalogId and sort each group by anatomical order.
 * Returns a Map (preserves insertion order of catalogs).
 */
export function groupAndSortAssembly(assembly: readonly IAssemblyEntry[]): Map<string, IAssemblyEntry[]> {
    const groups = new Map<string, IAssemblyEntry[]>();
    for (const entry of assembly) {
        const key = entry.catalogId || '';
        const group = groups.get(key);
        if (group) {
            group.push(entry);
        } else {
            groups.set(key, [entry]);
        }
    }
    for (const entries of groups.values()) {
        entries.sort((a, b) => assemblySortKey(a) - assemblySortKey(b));
    }
    return groups;
}
